<script lang="ts" setup>
import { useAppStore } from '@/store/app/app'
import { SnackbarType } from '@/store/app/app.types'
import { useBasketStore } from '@/store/basket/basket'
import { useCityStore } from '@/store/city/city'

import { ConstructorPizza, ProductPizza } from '@/types/api/api-v3/halves'

import HalvesConstructorTitle from '@/components/Halves/HalvesConstructorTitle.vue'
import WokAlert from '@/components/Wok/WokAlert.vue'

const cityStore = useCityStore()
const basketStore = useBasketStore()
const { showSnackbar } = useAppStore()

const { isDesktop } = useDevice()

type Props = {
  constructorPizza: ConstructorPizza
  wholePizza?: ProductPizza | undefined
}

const props = defineProps<Props>()

const visibleAlertNotification = ref(false)

const currencySymbol = computed(() => cityStore.currencySymbol)

const emptyConstructorPizza = computed(
  () =>
    (props.constructorPizza.leftHalve && !props.constructorPizza.rightHalve) ||
    (props.constructorPizza.rightHalve && !props.constructorPizza.leftHalve),
)

const isCalculateWeight = computed(() => {
  const { leftHalve, rightHalve } = props.constructorPizza

  if (props.wholePizza) {
    const { weight, measure } = props.wholePizza

    return `${weight} ${measure}`
  }

  if (leftHalve) return `${leftHalve.weight} ${leftHalve.measure}`

  if (rightHalve) return `${rightHalve.weight} ${rightHalve.measure}`

  return '0 г'
})

const isCalculatePrice = computed(() => {
  const { leftHalve, rightHalve } = props.constructorPizza

  if (props.wholePizza) {
    const { price } = props.wholePizza

    return `${price} ${currencySymbol.value}`
  }

  if (leftHalve) return `${leftHalve.price} ${currencySymbol.value}`

  if (rightHalve) return `${rightHalve.price} ${currencySymbol.value}`

  return `0 ${currencySymbol.value}`
})

const basketError = computed(() => basketStore.error)

watch(
  () => props.constructorPizza.rightHalve,
  () => (visibleAlertNotification.value = false),
)

watch(
  () => props.constructorPizza.leftHalve,
  () => (visibleAlertNotification.value = false),
)

const emits = defineEmits(['removeHalveOfConstructor'])

const handleAlertNotification = () => {
  if (isDesktop && emptyConstructorPizza.value) {
    visibleAlertNotification.value = true
  }
}

const addToPizza = async () => {
  if (props.wholePizza) await basketStore.addProduct(props.wholePizza.id)

  if (!basketError.value) {
    emits('removeHalveOfConstructor', 'leftHalve')
    emits('removeHalveOfConstructor', 'rightHalve')

    showSnackbar({
      message: 'Товар добавлен в корзину',
      type: SnackbarType.SUCCESS,
    })

    return
  }

  showSnackbar({
    message: 'Что-то пошло не так',
    type: SnackbarType.MESSAGE,
  })
}
</script>

<template>
  <section class="state-bar">
    <halves-constructor-title :constructor-pizza="constructorPizza" />

    <div class="state-bar__panel">
      <div class="state-bar__wrapper">
        <span class="state-bar__weight">{{ isCalculateWeight }}</span>
        <span class="state-bar__price">{{ isCalculatePrice }}</span>
      </div>
      <div class="state-bar__button" @click="handleAlertNotification">
        <the-button
          type="button"
          size="sm"
          radius="lg"
          block
          :disabled="!wholePizza"
          :loading="!basketStore.isLoaded"
          @click="addToPizza"
        >
          Хочу!
        </the-button>
      </div>
    </div>

    <transition name="ui-fade">
      <wok-alert v-if="visibleAlertNotification" class="state-bar__alert">
        Выберите вторую половинку
      </wok-alert>
    </transition>
  </section>
</template>

<style lang="scss" scoped>
.state-bar {
  &__panel {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
  }

  &__wrapper {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
  }

  &__weight {
    margin-right: 16px;
    font-size: 14px;
    font-weight: 500;
    line-height: 14px;
    color: $dark-grey;
  }

  &__price {
    font-size: 24px;
    font-weight: 800;
    line-height: 28px;
  }

  &__add-button {
    flex: 0 0 90px;
    font-weight: 700;
    border-radius: 40px;
  }

  &__button {
    width: 120px;
  }

  @include media('<=desktop-mini') {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 120px;
    border-top: 1px solid #e9eef4;

    &__alert {
      display: none;
    }

    &__panel {
      align-items: center;
    }

    &__wrapper {
      flex-direction: column;
      width: max-content;
    }

    &__price {
      font-size: 24px;
      line-height: 36px;
    }

    &__weight {
      order: 2;
      font-size: 14px;
      line-height: 20px;
    }

    &__button {
      margin-left: 80px;
    }
  }

  &__alert {
    width: 100%;
    margin-top: 40px;
  }

  @include media('<=tablet') {
    &__button {
      width: 90px;
      margin-left: 40px;
    }
  }

  @include media('<=portrait-phone') {
    &__weight,
    &__price {
      margin-right: 0;
      font-size: 14px;
      line-height: 20px;
    }

    &__button {
      margin-left: 30px;
    }
  }
}
</style>
